import { allParticipantPages } from 'Src/helpers/twoPanelEditor/participantFilters';
import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import propz from 'propz';
import { DEFAULT_FILTER } from '../admin/user';
import { DEFAULT_LIMIT, DEFAULT_SKIP, SORT } from '../../../consts/table';
import { Count } from '../../../models/count';
import { Tournament } from '../../../models/tournament';
import { TournamentCompetitionDefinition, TournamentCompetitionRuntime } from '../../../models/tournament';
import { getTodayMidnight } from 'Src/helpers/common/common';

const DEFAULT_TOURNAMENT_CREATOR_FILTER: any = {
  isCreator: true
};

const DEFAULT_TOURNAMENT_PARTICIPANT_FILTER: any = {
  isParticipant: true
};

const tournamentCountRequests = new Map<string, { expiresAt: number; promise: Promise<Count> }>();
const reuseCountRequest = (key: string, request: () => Promise<Count>): Promise<Count> => {
  const cached = tournamentCountRequests.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;
  const promise = request().catch(error => {
    tournamentCountRequests.delete(key);
    throw error;
  });
  tournamentCountRequests.set(key, { expiresAt: Date.now() + 3000, promise });
  return promise;
};

export type AdminOption = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  permissionId?: string;
};

export const getFullName = (u: { firstName?: string; lastName?: string }) =>
  [u?.firstName, u?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

export function saveTournamentCompetitionStructureDraft(
  user: AppUser,
  tournamentId: string,
  data: TournamentCompetitionDefinition
): Promise<Tournament> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.put(
    `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/competitionStructure/draft`,
    data,
    { headers: { usid: key } }
  ).then(response => response.data);
}

export interface TournamentStructurePublishPreview {
  token: string;
  fixturesRemoved: number;
  resultsRemoved: number;
  rows: Array<{groupId: string; name: string; fixturesRemoved: number; resultsRemoved: number; plannedMatches: number}>;
}

export function previewTournamentCompetitionStructure(user: AppUser, tournamentId: string): Promise<TournamentStructurePublishPreview> {
  return axios.post(`${window.apiBase}/i/schools/${user.activeSchoolId}/tournaments/${tournamentId}/competitionStructure/publish-preview`,
    {}, {headers: {usid: user.roleSessionKey}}).then(response => response.data);
}

export function publishTournamentCompetitionStructure(user: AppUser, tournamentId: string, previewToken?: string): Promise<Tournament> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.post(
    `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/competitionStructure/publish`,
    previewToken ? {previewToken} : {},
    { headers: { usid: key } }
  ).then(response => response.data);
}

export function getTournamentCompetitionRuntime(user: AppUser, tournamentId: string, published = false): Promise<TournamentCompetitionRuntime> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.get(
    `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/competitionStructure/runtime`,
    { headers: { usid: key }, params: published ? {published: true} : undefined }
  ).then(response => response.data);
}

export function saveTournamentGroupOrder(user: AppUser, tournamentId: string, groupId: string, teamIds: string[], resultHash: string): Promise<TournamentCompetitionRuntime> {
  return axios.put(`${window.apiBase}/i/schools/${user.activeSchoolId}/tournaments/${tournamentId}/competitionStructure/groups/${groupId}/order`,
    {teamIds, resultHash}, {headers: {usid: user.roleSessionKey}}).then(response => response.data);
}

export function syncTournamentCompetitionStructure(user: AppUser, tournamentId: string): Promise<{runtime:TournamentCompetitionRuntime;createdEventIds:string[]}> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.post(
    `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/competitionStructure/sync`, {},
    { headers: { usid: key } }
  ).then(response => response.data);
}

export function confirmTournamentGroupAdvancement(user: AppUser, tournamentId: string, groupIds?: string[], groupResultHashes?: Record<string, string>): Promise<{runtime:TournamentCompetitionRuntime;createdEventIds:string[]}> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.post(
    `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/competitionStructure/confirm-group-advancement`, groupIds ? {groupIds,groupResultHashes} : {},
    { headers: { usid: key } }
  ).then(response => response.data);
}

export const getFullNameWithEmail = (u: { firstName?: string; lastName?: string; email?: string }) => {
  const name = getFullName(u);
  return u?.email ? (name ? `${name} — ${u.email}` : u.email) : name || '';
};

function getUpcomingTournamentsStartTimeFilterField(filter) {
  const todayMidnight = getTodayMidnight();

  const startTimeFromFilter = propz.get(filter, ['where', 'startTime']);
  const isStartTimeFromFilterExist = typeof startTimeFromFilter !== 'undefined';
  const isStartTimeFromFilterLessThanCurrentDate =
    isStartTimeFromFilterExist && new Date(startTimeFromFilter['$gte']) <= new Date(todayMidnight);

  const startTime = isStartTimeFromFilterLessThanCurrentDate
    ? { ...startTimeFromFilter, $gte: todayMidnight }
    : startTimeFromFilter;

  return isStartTimeFromFilterExist ? startTime : { $gte: todayMidnight };
}

function getPastTournamentsStartTimeFilterField(filter) {
  const todayMidnight = getTodayMidnight();

  const startTimeFromFilter = propz.get(filter, ['where', 'startTime']);
  const isStartTimeFromFilterExist = typeof startTimeFromFilter !== 'undefined';
  const isStartTimeFromFilterMoreThanCurrentDate =
    isStartTimeFromFilterExist && new Date(startTimeFromFilter['$lte']) >= new Date(todayMidnight);

  const startTime = isStartTimeFromFilterMoreThanCurrentDate
    ? { ...startTimeFromFilter, $lte: todayMidnight }
    : startTimeFromFilter;

  return isStartTimeFromFilterExist ? startTime : { $lte: todayMidnight };
}

export function getTournaments(user: AppUser, filter = DEFAULT_TOURNAMENT_CREATOR_FILTER): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order
      }
    }
  };

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getUpcomingTournaments(
  user: AppUser,
  filter = DEFAULT_TOURNAMENT_CREATOR_FILTER,
  unansweredConfirmationRequests = false
): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const startTime = getUpcomingTournamentsStartTimeFilterField(filter);

  const config = {
    headers: { usid: key },
    params: {
      summary: true,
      unansweredConfirmationRequests: unansweredConfirmationRequests || undefined,
      filter: {
        where: {
          ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
          ...filter.where,
          startTime
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order || `startTime ${SORT.ASC}`
      }
    }
  };

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getPastTournaments(user: AppUser, filter = DEFAULT_TOURNAMENT_CREATOR_FILTER): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const startTime = getPastTournamentsStartTimeFilterField(filter);

  const config = {
    headers: { usid: key },
    params: {
      summary: true,
      filter: {
        where: {
          ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
          ...filter.where,
          startTime
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order || `startTime ${SORT.ASC}`
      }
    }
  };

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getHighLevelTournaments(user: AppUser, filter): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order
      }
    }
  };

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getUpcomingHighLevelTournaments(user: AppUser, filter): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const startTime = getUpcomingTournamentsStartTimeFilterField(filter);

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
          ...filter.where,
          startTime
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order || `startTime ${SORT.ASC}`
      }
    }
  };

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getPastHighLevelTournaments(user: AppUser, filter): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const startTime = getPastTournamentsStartTimeFilterField(filter);

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
          ...filter.where,
          startTime
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order || `startTime ${SORT.ASC}`
      }
    }
  };

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getHighLevelTournamentsCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
            ...where
          }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER
          }
        }
      }
    };
  }

  return axios.get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/count`, config).then(response => {
    return response.data;
  });
}

export function getUpcomingHighLevelTournamentsCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const startTime = getUpcomingTournamentsStartTimeFilterField({ where });

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
            ...where,
            startTime
          }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
            startTime
          }
        }
      }
    };
  }

  return axios.get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/count`, config).then(response => {
    return response.data;
  });
}

export function getPastHighLevelTournamentsCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const startTime = getPastTournamentsStartTimeFilterField({ where });

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
            ...where,
            startTime
          }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
            startTime
          }
        }
      }
    };
  }

  return axios.get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/count`, config).then(response => {
    return response.data;
  });
}

export async function getAllHighLevelTournamentTeamPlayers(user: AppUser, tournamentId: string, teamId: string, where?) {
  const result = [];
  for (let skip = 0; ; skip += DEFAULT_LIMIT) {
    const rows = await getHighLevelTournamentTeamPlayers(user, tournamentId, teamId, {where, limit: DEFAULT_LIMIT, skip});
    result.push(...rows);
    if (rows.length < DEFAULT_LIMIT) return result;
  }
}

export function getHighLevelTournamentTeamPlayers(user: AppUser, tournamentId: string, teamId: string, filter) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...filter.where
        },
        limit: filter.limit,
        order: filter.order,
        skip: filter.skip
      }
    }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/teams/${teamId}/players`, config)
    .then(response => {
      return response.data;
    });
}

export function getHighLevelTournamentTeamPlayersCount(
  user: AppUser,
  tournamentId: string,
  teamId: string,
  where
): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
            ...where
          }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER
          }
        }
      }
    };
  }

  return axios
    .get<Count>(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/teams/${teamId}/players/count`,
      config
    )
    .then(response => {
      return response.data;
    });
}

export function getAllHighLevelTournaments(user: AppUser, where?): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getAllUpcomingHighLevelTournaments(user: AppUser, where?): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  const todayMidnight = getTodayMidnight();

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            startTime: { $gte: todayMidnight },
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            startTime: { $gte: todayMidnight },
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getAllPastHighLevelTournaments(user: AppUser, where?): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  const todayMidnight = getTodayMidnight();

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            startTime: { $lte: todayMidnight },
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            startTime: { $lte: todayMidnight },
            ...DEFAULT_TOURNAMENT_PARTICIPANT_FILTER
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getAllTournaments(user: AppUser, where?): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getAllUpcomingTournaments(user: AppUser, where?): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  const todayMidnight = getTodayMidnight();

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            startTime: { $gte: todayMidnight },
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            startTime: { $gte: todayMidnight },
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getAllPastTournaments(user: AppUser, where?): Promise<Tournament[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  const todayMidnight = getTodayMidnight();

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            startTime: { $lte: todayMidnight },
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            startTime: { $lte: todayMidnight },
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get<Tournament[]>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, config).then(response => {
    return response.data;
  });
}

export function getTournamentsCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
            ...where
          }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER
          }
        }
      }
    };
  }

  return axios.get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/count`, config).then(response => {
    return response.data;
  });
}

export function getUpcomingTournamentsCount(
  user: AppUser,
  where?,
  unansweredConfirmationRequests = false
): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const startTime = getUpcomingTournamentsStartTimeFilterField({ where });

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        unansweredConfirmationRequests: unansweredConfirmationRequests || undefined,
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
            ...where,
            startTime
          }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        unansweredConfirmationRequests: unansweredConfirmationRequests || undefined,
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
            startTime
          }
        }
      }
    };
  }

  const request = () =>
    axios
      .get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/count`, config)
      .then(response => response.data);
  const isUnfiltered = typeof where === 'undefined' || Object.keys(where || {}).length === 0;
  return isUnfiltered
    ? reuseCountRequest(`upcoming:${activeSchoolId}:${Boolean(unansweredConfirmationRequests)}`, request)
    : request();
}

export function getUpcomingTournamentsWithUnansweredConfirmationRequestsCount(user: AppUser): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/confirmationRequests/unanswered/count`, {
      headers: { usid: key }
    })
    .then(response => response.data);
}

export interface UnionTournamentListSummary {
  upcomingCount: number;
  archiveCount: number;
  invitesCount: number;
  unansweredConfirmationCount: number;
  nextTournament?: {
    id: string;
    startTime: string;
  };
}

const unionTournamentListSummaryRequests = new Map<
  string,
  { expiresAt: number; request: Promise<UnionTournamentListSummary> }
>();

export function getUnionTournamentListSummary(user: AppUser): Promise<UnionTournamentListSummary> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const cacheKey = String(activeSchoolId);
  const cached = unionTournamentListSummaryRequests.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.request;
  const request = axios
    .get<UnionTournamentListSummary>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/list-summary`, {
      headers: { usid: key }
    })
    .then(response => response.data)
    .catch(error => {
      unionTournamentListSummaryRequests.delete(cacheKey);
      throw error;
    });
  unionTournamentListSummaryRequests.set(cacheKey, { expiresAt: Date.now() + 60000, request });
  return request;
}

export interface TournamentManagementSummary {
  eligible: number;
  notInvited: number;
  accepted: number;
  rejected: number;
  participants: number;
  schoolAdmins: number;
  teams: number;
  waiting: number;
  confirmationTotal: number;
  confirmationPending: number;
  confirmationRejected: number;
  confirmationOutdated: number;
  logs: number;
}

const managementSummaryRequests = new Map<
  string,
  { expiresAt: number; request: Promise<TournamentManagementSummary> }
>();

export function invalidateTournamentManagementSummary(user: AppUser, tournamentId: string): void {
  managementSummaryRequests.delete(`${user.activeSchoolId}:${tournamentId}`);
}

export function getTournamentManagementSummary(
  user: AppUser,
  tournamentId: string,
  force = false
): Promise<TournamentManagementSummary> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const cacheKey = `${activeSchoolId}:${tournamentId}`;
  const cached = managementSummaryRequests.get(cacheKey);
  if (!force && cached && cached.expiresAt > Date.now()) return cached.request;

  const request = axios
    .get<TournamentManagementSummary>(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/management-summary`,
      { headers: { usid: key } }
    )
    .then(response => response.data)
    .catch(error => {
      managementSummaryRequests.delete(cacheKey);
      throw error;
    });
  managementSummaryRequests.set(cacheKey, { expiresAt: Date.now() + 60000, request });
  return request;
}

export function getPastTournamentsCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const startTime = getPastTournamentsStartTimeFilterField({ where });

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
            ...where,
            startTime
          }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_TOURNAMENT_CREATOR_FILTER,
            startTime
          }
        }
      }
    };
  }

  return axios.get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/count`, config).then(response => {
    return response.data;
  });
}

export function getTournament(user: AppUser, tournamentId: string, summary = false): Promise<Tournament> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = { headers: { usid: key }, params: { summary: summary ? true : undefined } };

  return axios
    .get<Tournament>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}`, config)
    .then(response => {
      return response.data;
    });
}

export function createTournament(user: AppUser, data: any) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios.post(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments`, data, config).then(response => {
    return response.data;
  });
}

export function deleteTournament(user: AppUser, tournamentId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}`, config)
    .then(response => {
      return response.data;
    });
}

export function getTournamentDeletionCheck(
  user: AppUser,
  tournamentId: string
): Promise<{ canDelete: boolean; blockers: string[] }> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/deletion-check`, {
      headers: { usid: key }
    })
    .then(response => response.data);
}

export function getTournamentAthleteSchools(user: AppUser, tournamentId: string, isCreator: boolean, filter: any = {}) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      isCreator: isCreator ? 'true' : undefined,
      filter
    }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/athleteSchools`, config)
    .then(response => {
      return response.data;
    });
}

export function getTournamentAthleteSchoolsCount(
  user: AppUser,
  tournamentId: string,
  isCreator: boolean,
  where: any = {}
) {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/athleteSchools/count`, {
      headers: { usid: key },
      params: {
        isCreator: isCreator ? 'true' : undefined,
        filter: { where }
      }
    })
    .then(response => response.data);
}

// Large exclusion lists must travel in the body, beyond proxy URL and query-parser limits.
function requestTournamentEligibleSchools(user: AppUser, tournamentId, filter: any, count = false) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const url = `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/eligibleSchools${count ? '/count' : ''}`;
  const config = { headers: { usid: key } };
  const request = JSON.stringify(filter).length > 1500
    ? axios.post(`${url}/search`, { filter }, config)
    : axios.get(url, { ...config, params: { filter } });
  return request.then(response => response.data);
}

export function getTournamentEligibleSchools(user: AppUser, tournamentId, filter: any = DEFAULT_FILTER) {
  return requestTournamentEligibleSchools(user, tournamentId, filter);
}

export function getTournamentEligibleSchoolsCount(user: AppUser, tournamentId, where = {}): Promise<Count> {
  return requestTournamentEligibleSchools(user, tournamentId, { where }, true);
}

export function getAllTournamentEligibleSchools(user: AppUser, tournamentId, where = {}) {
  return allParticipantPages<any>(filter => getTournamentEligibleSchools(user, tournamentId, filter), {
    where,
    order: ['name ASC', '_id ASC']
  });
}

export function updateTournamentAutoEnrollment(user: AppUser, data, tournamentId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/autoEnrollmentTeamsSettings`,
      data,
      config
    )
    .then(response => {
      return response.data;
    });
}

export function getCsvListTournamentStudents(user: AppUser, tournamentId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/players/csv`, config)
    .then(response => {
      return response.data;
    });
}

export function updateTournament(user: AppUser, data, tournamentId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}`, data, config)
    .then(response => {
      return response.data;
    });
}

export function checkTournamentDomain(user: AppUser, domain: string, tournamentId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/domains/check`,
      { domain, tournamentId },
      { headers: { usid: key } }
    )
    .then(response => response.data as { isAvailable: boolean });
}

export function getTournamentCsv(user: AppUser, tournamentId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    responseType: 'blob' as 'blob'
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/csv`, config)
    .then(response => response.data);
}

export function getTournamentMeetPack(user: AppUser, tournamentId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const apiBase = String(window.apiBase || '').replace(/\/$/, '');
  return axios
    .get(`${apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/meet-pack`, {
      headers: { usid: key }, responseType: 'blob' as const
    })
    .then(response => response.data);
}

export function getTournamentCompetitors(user: AppUser, tournamentId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/players`, {
      headers: { usid: key },
      params: { filter: { limit: 10000, skip: 0 } }
    })
    .then(response => response.data);
}

export function updateTournamentCompetitorNumbers(
  user: AppUser,
  tournamentId: string,
  updates: Array<{ teamId: string; playerId: string; quantumPlayerNumber: number }>
) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/players/quantumNumbers/batch`,
      { updates }, { headers: { usid: key } })
    .then(response => response.data as { updated: number });
}

export function updateTournamentCompetitorNumber(user: AppUser, tournamentId: string, teamId: string, playerId: string, quantumPlayerNumber: number) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/teams/${teamId}/players/${playerId}`,
      { quantumPlayerNumber }, { headers: { usid: key } })
    .then(response => response.data);
}

export function assignTournamentCompetitorNumbers(user: AppUser, tournamentId: string, startFrom: number) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/players/assignQuantumNumbers`,
      { startFrom }, { headers: { usid: key } })
    .then(response => response.data);
}

export function unsubscribeTournament(user: AppUser, tournamentId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/unsubscribe`, {}, config)
    .then(response => {
      return response.data;
    });
}

export function downloadTournamentMeetPack(user: AppUser, tournamentId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    responseType: 'blob' as const
  };

  const apiBase = String((window as any).apiBase || '').replace(/\/$/, '');
  return axios
    .get(`${apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/meet-pack`, config)
    .then(res => {
      const cd =
        (res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) ||
        (typeof (res.headers as any)?.get === 'function' ? (res.headers as any).get('Content-Disposition') : undefined);

      let filename = `meet-pack.zip`;
      if (cd) {
        const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
        const raw = decodeURIComponent((m?.[1] || m?.[2] || '').trim());
        if (raw) filename = raw;
      }

      return { blob: res.data as Blob, filename };
    });
}

export function assignTournamentEventNumbers(user: AppUser, tournamentId: string, startFrom: number) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(
      `${(window as any).apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/events/assignQuantumNumbers`,
      { startFrom },
      config
    )
    .then(res => res.data);
}

export function getAdmins(user: AppUser, filter) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/users/admins`, config).then(response => {
    return response.data;
  });
}
